import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { AuthProvider } from './context/AuthContext';
import { LanguageProvider } from './i18n/LanguageContext';
import PrivateRoute from './components/common/PrivateRoute';
import Layout from './components/layout/Layout';
import Login from './components/auth/Login';
import Dashboard from './pages/Dashboard';
import ProductList from './pages/products/ProductList';
import ProductForm from './pages/products/ProductForm';
import CustomerList from './pages/customers/CustomerList';
import CustomerForm from './pages/customers/CustomerForm';
import SalesOrderList from './pages/sales/SalesOrderList';
import SalesOrderForm from './pages/sales/SalesOrderForm';
import InvoiceList from './pages/invoices/InvoiceList';
import InvoiceForm from './pages/invoices/InvoiceForm';
import EmployeeList from './pages/employees/EmployeeList';
import EmployeeForm from './pages/employees/EmployeeForm';
import AccountList from './pages/accounting/AccountList';
import AccountForm from './pages/accounting/AccountForm';
import JournalEntryList from './pages/accounting/JournalEntryList';
import JournalEntryForm from './pages/accounting/JournalEntryForm';
import Reports from './pages/accounting/Reports';
import PostingProfiles from './pages/accounting/PostingProfiles';
import SubledgerView from './pages/accounting/SubledgerView';
import SupplierList from './pages/procurement/SupplierList';
import SupplierForm from './pages/procurement/SupplierForm';
import PurchaseOrderList from './pages/procurement/PurchaseOrderList';
import PurchaseOrderForm from './pages/procurement/PurchaseOrderForm';
import GoodsReceiptList from './pages/procurement/GoodsReceiptList';
import GoodsReceiptForm from './pages/procurement/GoodsReceiptForm';
import PurchaseInvoiceList from './pages/procurement/PurchaseInvoiceList';
import PurchaseInvoiceForm from './pages/procurement/PurchaseInvoiceForm';
import Settings from './pages/settings/Settings';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <LanguageProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />

            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </PrivateRoute>
              }
            />

            {/* Products */}
            <Route
              path="/products"
              element={
                <PrivateRoute>
                  <Layout>
                    <ProductList />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/products/new"
              element={
                <PrivateRoute>
                  <Layout>
                    <ProductForm />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/products/edit/:id"
              element={
                <PrivateRoute>
                  <Layout>
                    <ProductForm />
                  </Layout>
                </PrivateRoute>
              }
            />

            {/* Customers */}
            <Route
              path="/customers"
              element={
                <PrivateRoute>
                  <Layout>
                    <CustomerList />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/customers/new"
              element={
                <PrivateRoute>
                  <Layout>
                    <CustomerForm />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/customers/edit/:id"
              element={
                <PrivateRoute>
                  <Layout>
                    <CustomerForm />
                  </Layout>
                </PrivateRoute>
              }
            />

            {/* Sales Orders */}
            <Route
              path="/sales-orders"
              element={
                <PrivateRoute>
                  <Layout>
                    <SalesOrderList />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/sales-orders/new"
              element={
                <PrivateRoute>
                  <Layout>
                    <SalesOrderForm />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/sales-orders/:id"
              element={
                <PrivateRoute>
                  <Layout>
                    <SalesOrderForm />
                  </Layout>
                </PrivateRoute>
              }
            />

            {/* Invoices */}
            <Route
              path="/invoices"
              element={
                <PrivateRoute>
                  <Layout>
                    <InvoiceList />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/invoices/new"
              element={
                <PrivateRoute>
                  <Layout>
                    <InvoiceForm />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/invoices/edit/:id"
              element={
                <PrivateRoute>
                  <Layout>
                    <InvoiceForm />
                  </Layout>
                </PrivateRoute>
              }
            />

            {/* Employees */}
            <Route
              path="/employees"
              element={
                <PrivateRoute>
                  <Layout>
                    <EmployeeList />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/employees/new"
              element={
                <PrivateRoute>
                  <Layout>
                    <EmployeeForm />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/employees/edit/:id"
              element={
                <PrivateRoute>
                  <Layout>
                    <EmployeeForm />
                  </Layout>
                </PrivateRoute>
              }
            />

            {/* Accounting */}
            <Route
              path="/accounts"
              element={
                <PrivateRoute>
                  <Layout>
                    <AccountList />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/accounts/new"
              element={
                <PrivateRoute>
                  <Layout>
                    <AccountForm />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/accounts/edit/:id"
              element={
                <PrivateRoute>
                  <Layout>
                    <AccountForm />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/journal-entries"
              element={
                <PrivateRoute>
                  <Layout>
                    <JournalEntryList />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/journal-entries/new"
              element={
                <PrivateRoute>
                  <Layout>
                    <JournalEntryForm />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/journal-entries/:id"
              element={
                <PrivateRoute>
                  <Layout>
                    <JournalEntryForm />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/reports"
              element={
                <PrivateRoute>
                  <Layout>
                    <Reports />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/posting-profiles"
              element={
                <PrivateRoute>
                  <Layout>
                    <PostingProfiles />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/subledger"
              element={
                <PrivateRoute>
                  <Layout>
                    <SubledgerView />
                  </Layout>
                </PrivateRoute>
              }
            />

            {/* Procurement - Suppliers */}
            <Route
              path="/suppliers"
              element={
                <PrivateRoute>
                  <Layout>
                    <SupplierList />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/suppliers/new"
              element={
                <PrivateRoute>
                  <Layout>
                    <SupplierForm />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/suppliers/edit/:id"
              element={
                <PrivateRoute>
                  <Layout>
                    <SupplierForm />
                  </Layout>
                </PrivateRoute>
              }
            />

            {/* Procurement - Purchase Orders */}
            <Route
              path="/purchase-orders"
              element={
                <PrivateRoute>
                  <Layout>
                    <PurchaseOrderList />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/purchase-orders/new"
              element={
                <PrivateRoute>
                  <Layout>
                    <PurchaseOrderForm />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/purchase-orders/:id"
              element={
                <PrivateRoute>
                  <Layout>
                    <PurchaseOrderForm />
                  </Layout>
                </PrivateRoute>
              }
            />

            {/* Procurement - Goods Receipts */}
            <Route
              path="/goods-receipts"
              element={
                <PrivateRoute>
                  <Layout>
                    <GoodsReceiptList />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/goods-receipts/new"
              element={
                <PrivateRoute>
                  <Layout>
                    <GoodsReceiptForm />
                  </Layout>
                </PrivateRoute>
              }
            />

            {/* Settings */}

            {/* Purchase Invoices (Bills) */}
            <Route
              path="/purchase-invoices"
              element={
                <PrivateRoute>
                  <Layout>
                    <PurchaseInvoiceList />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/purchase-invoices/new"
              element={
                <PrivateRoute>
                  <Layout>
                    <PurchaseInvoiceForm />
                  </Layout>
                </PrivateRoute>
              }
            />

            {/* Settings - Page */}
            <Route
              path="/settings"
              element={
                <PrivateRoute>
                  <Layout>
                    <Settings />
                  </Layout>
                </PrivateRoute>
              }
            />

            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Router>
        </LanguageProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
